import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import {
  Breadcrumbs,
  PageHeader,
  Button,
  Tag,
  Menu,
  Plus,
  DotsVertical,
  Edit01,
  Archive,
  AvatarLabelGroup,
  Users01,
} from "@opendate-app/setlist";
import EmptyStateCard from "../ClassesShared/EmptyStateCard";
import IndexToolbar, { IndexOverlays, IndexTable } from "../ClassesShared/IndexToolbar";
import useClassesIndexTable, { ClassesIndexTableContext, archivedStatusQuery } from "../ClassesShared/useClassesIndexTable";
import { formatAdded } from "../ClassesShared/classLabels";
import "../ClassesShared/classesShared.css";
import initialsFor from "../ClassesShared/initials";
import personColorProps from "../ClassesShared/personColor";
import { INSTRUCTOR_FILTER_GROUPS } from "./constants/instructorFiltersConfig";
import { INSTRUCTOR_COLUMNS, SORT_OPTIONS } from "./constants/instructorColumnsConfig";

function InstructorsApp(props) {
  const { indexPath, classesPath, csrfToken, canManage, teamId, skillsOptions = [], totalCount } = props;

  const filterGroups = useMemo(
    () =>
      INSTRUCTOR_FILTER_GROUPS.map((group) => ({
        ...group,
        filters: group.filters.map((filter) =>
          filter.id === "skills"
            ? { ...filter, options: skillsOptions.map((s) => ({ value: s, label: s })) }
            : filter
        ),
      })),
    [skillsOptions]
  );

  const table = useClassesIndexTable({
    indexPath,
    csrfToken,
    teamId,
    storageKey: "instructor",
    dataKey: "instructors",
    noun: "instructors",
    kind: "instructor",
    columns: INSTRUCTOR_COLUMNS,
    sortOptions: SORT_OPTIONS,
    filterGroups,
    searchKey: "name_or_email_cont",
    buildQuery: (filters) => ({
      ...archivedStatusQuery(filters.status),
      ...(filters.skills?.length ? { with_any_skill: filters.skills } : {}),
    }),
  });
  const { requestArchive } = table;

  const columns = useMemo(() => {
    const cols = table.visibleColumns.map((col) => {
      switch (col.id) {
        case "instructor":
          return {
            key: "instructor",
            header: "Instructor",
            primary: true,
            render: (i) => (
              <a href={`${indexPath}/${i.id}`} className="text-reset text-decoration-none">
                <AvatarLabelGroup
                  size="sm"
                  name={i.name}
                  supportingText={i.email || undefined}
                  src={i.photo_url || undefined}
                  initials={initialsFor(i.name)}
                  {...personColorProps(i.name)}
                />
              </a>
            ),
          };
        case "skills":
          return {
            key: "skills",
            header: "Skills",
            render: (i) =>
              (i.skills || []).length > 0 ? (
                <span className="d-flex flex-wrap classes-gap-6">
                  {i.skills.map((skill) => (
                    <Tag key={skill} size="sm">
                      {skill}
                    </Tag>
                  ))}
                </span>
              ) : (
                "—"
              ),
          };
        case "upcoming_sessions_count":
          return {
            key: "upcoming_sessions_count",
            header: "Upcoming sessions",
            render: (i) => i.upcoming_sessions_count ?? 0,
          };
        case "all_time_sessions_count":
          return {
            key: "all_time_sessions_count",
            header: "All-time sessions",
            render: (i) => i.all_time_sessions_count ?? 0,
          };
        case "created_at":
          return { key: "created_at", header: "Added", render: (i) => formatAdded(i.created_at) };
        case "title":
          return { key: "title", header: "Title", render: (i) => i.title || "—" };
        case "email":
          return { key: "email", header: "Email", render: (i) => i.email || "—" };
        default:
          return { key: col.id, header: col.label, render: () => "—" };
      }
    });

    if (canManage) {
      cols.push({
        key: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "right",
        render: (i) => (
          <Menu
            placement="bottom-end"
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${i.name}`} />}
            items={[
              { label: "Edit Instructor", icon: Edit01, onClick: () => { window.location.href = `${indexPath}/${i.id}/edit`; } },
              {
                label: i.archived ? "Unarchive Instructor" : "Archive Instructor",
                icon: Archive,
                onClick: () => requestArchive(i),
              },
            ]}
          />
        ),
      });
    }
    return cols;
  }, [table.visibleColumns, indexPath, canManage, requestArchive]);

  return (
    <ClassesIndexTableContext.Provider value={table}>
      <div className="od-themed p-4">
        <PageHeader
          breadcrumbs={<Breadcrumbs items={[{ label: "Classes", href: classesPath }, { label: "Instructors" }]} />}
          title={<span role="heading" aria-level={1}>Instructors</span>}
          supportingText="Trainers who teach your classes."
          actions={
            canManage && (
              <Button as="a" variant="primary" icon={Plus} href={`${indexPath}/new`}>
                New Instructor
              </Button>
            )
          }
        />

        {totalCount === 0 ? (
          <div className="mt-4">
            <EmptyStateCard
              icon={Users01}
              title="Add the people who teach"
              description="Instructors are assigned to class sessions and their profiles appear on public class pages. Add one to start building your teaching roster."
              {...(canManage ? { primaryAction: { label: "Add Your First Instructor", icon: Plus, onClick: () => { window.location.href = `${indexPath}/new`; } } } : {})}
            />
          </div>
        ) : (
          <>
            <IndexToolbar />
            <IndexTable columns={columns} empty="No instructors yet." />
          </>
        )}

        <IndexOverlays />
        <ToastContainer icon={false} />
      </div>
    </ClassesIndexTableContext.Provider>
  );
}

InstructorsApp.propTypes = {
  indexPath: PropTypes.string.isRequired,
  classesPath: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  skillsOptions: PropTypes.arrayOf(PropTypes.string),
  totalCount: PropTypes.number,
};

export default InstructorsApp;
